import { useContext } from "react";
import { useSelector } from "react-redux";

import FilterWrapper from "@components/store/products/listing-header/filter-wrapper";
import { Panel, PanelCollapsibleArea, PanelLabel } from "@components/store/sidebar/list-panel";
import CheckboxList from "@components/store/sidebar/checkbox-listing";
import RadioInputList from "@components/store/sidebar/radio-input-listing";
import PriceRangeSlider from "@components/store/sidebar/price-range-slider";
import { ProductListingContext } from "@components/store/products/listing/listing-provider";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import { ExternalButton, InternalButton } from "@sharedComponents/buttons";
import { CATALOG_REQUEST_ITEM_TYPE_FORM_LINK } from "@stadium/shared/src/constants/stores/routes";

import { getIsLockerShop, getStoreNumber } from "@selectors/store";

import { useWithAnalytics } from "@stadium/services/analytics/hooks/use-with-analytics";

import { pluralizeWord } from "@stadium/helpers/string";
import { getNestedOptionsData } from "@helpers/filters";

import { PRICE_FILTER_KEY } from "@constants/products/listing";
import { ANALYTICS_EVENT_ACTION_OPENED } from "@stadium/services/analytics/constants/default";
import {
  ANALYTICS_TARGET_FILTER_SECTION,
  ANALYTICS_ACTION_SECTION_COLLAPSED,
} from "@constants/analytics/filter";

import styles from "./main.module.scss";

const PRICE_FILTER_EVENT_KEY = "price";

export const FilterList = ({
  data,
  priceData,
  isMultiLevelMenu,
  isFetchingProducts,
  categoryListing = "",
  title,
  maxItemsToShow,
  onViewAll,
}) => {
  const isLockerShop = useSelector(getIsLockerShop);
  const storeNumber = useSelector(getStoreNumber);

  const withAnalytics = useWithAnalytics();

  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const { isPriceRangeFilterEnabled, totalProducts, totalFiltersApplied, setFilterToDefault } =
    useContext(ProductListingContext);

  const renderClearAllFiters = () => (
    <InternalButton onClick={setFilterToDefault} styleType="link" className={styles.clearAll}>
      CLEAR ALL
    </InternalButton>
  );

  const filterTitle = title ?? `Filters ${totalFiltersApplied ? `(${totalFiltersApplied})` : ""}`;

  const handleChecked = (key, id, onChange, childIds, event) => {
    onChange({ key, id, childIds, inputType: event.target.type });
  };
  const renderPriceRangeSlider = (defaultOpen) => {
    if (!priceData?.label) return null;
    const { key, ...restPriceData } = priceData;
    return (
      <Panel
        eventKey={PRICE_FILTER_EVENT_KEY}
        defaultOpen={defaultOpen}
        className={styles.filterByDropdown}
        onSelect={(selectedKey) => {
          if (!storeNumber) return;
          const isOpening = selectedKey === PRICE_FILTER_EVENT_KEY;
          withAnalytics(undefined, {
            track: true,
            target: ANALYTICS_TARGET_FILTER_SECTION,
            action: isOpening ? ANALYTICS_EVENT_ACTION_OPENED : ANALYTICS_ACTION_SECTION_COLLAPSED,
            eventProperties: {
              shop_number: storeNumber,
              filter_type: PRICE_FILTER_EVENT_KEY,
            },
          })();
        }}
      >
        <PanelLabel
          disabled={false}
          id={PRICE_FILTER_EVENT_KEY}
          className={`${styles.panelLabel} ${isPriceRangeFilterEnabled ? styles.activeFilter : ""}`}
        >
          {restPriceData.label}
        </PanelLabel>
        <PanelCollapsibleArea
          eventKey={PRICE_FILTER_EVENT_KEY}
          className={`${styles.dropdownFilter} ${styles.filterDropdownItem}`}
        >
          <div className={styles.pointsPanel}>
            <PriceRangeSlider
              key={key}
              {...restPriceData}
              isFetchingProducts={isFetchingProducts}
            />
            {isPriceRangeFilterEnabled && (
              <div className={`${styles.rangeResultText}`}>
                {totalProducts} {pluralizeWord("result", { count: totalProducts })} in range
              </div>
            )}
          </div>
        </PanelCollapsibleArea>
      </Panel>
    );
  };

  const renderMenuItem = (itemDetails, index) => {
    const {
      key,
      items,
      onChange,
      label,
      selectedIds,
      type = "checkbox",
      defaultOpen,
    } = itemDetails;
    if (key === PRICE_FILTER_KEY) return renderPriceRangeSlider(defaultOpen);
    if (items?.length > 0) {
      const inputParams = {
        key,
        items: getNestedOptionsData(items),
        onChange,
        label:
          selectedIds?.length && type === "checkbox" ? (
            <div className={styles.filterListItem}>
              {label} <span className={styles.filterCount}>{selectedIds?.length}</span>
            </div>
          ) : (
            label
          ),
        placeholder: "Search here",
        selectedIds,
        handleChecked,
        disabled: false,
        defaultOpen,
        maxItemsToShow,
      };

      if (type === "checkbox")
        return (
          <CheckboxList
            {...inputParams}
            id={key}
            onViewAll={(e) => {
              onViewAll?.(itemDetails);
            }}
          />
        );
      else if (type === "radioButton") return <RadioInputList {...inputParams} id={key} />;
    }
  };

  const hasFilterItems =
    data.some(({ items }) => items?.length) || typeof priceData?.items?.max !== "undefined";

  return (
    <>
      {isMultiLevelMenu && categoryListing}
      {hasFilterItems && (
        <>
          <div className={styles.filterByContainer}>
            {filterTitle && (
              <div className={styles.filterTitleBar}>
                <div className={styles.filterByTitle}>{filterTitle}</div>
                {!isMobileDevice && Boolean(totalFiltersApplied) && renderClearAllFiters()}
              </div>
            )}
            {data.map(renderMenuItem)}
          </div>
          {isLockerShop && !isMobileDevice && (
            <div className={styles.buttonsBlock}>
              <ExternalButton
                href={CATALOG_REQUEST_ITEM_TYPE_FORM_LINK}
                styleType="primary-outline"
                className={styles.requestLink}
                target="_blank"
              >
                Request Expert Swag Help
              </ExternalButton>
            </div>
          )}
        </>
      )}
    </>
  );
};

const SidebarFiltersListing = ({ hideFilterKeys, ...restProps }) => {
  return (
    <FilterWrapper Component={FilterList} childProps={restProps} hideFilterKeys={hideFilterKeys} />
  );
};

export default SidebarFiltersListing;
